import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/Header';
import TelaDaExecucao from '../components/home/TelaDaExecucao';
import Fecho from '../components/home/Fecho';
import useRevelar from '../components/home/useRevelar';
import Calculadora from '../components/blog/Calculadora';
import Chamada from '../components/blog/Chamada';
import LinhaDoPost from '../components/blog/LinhaDoPost';
import NotFound from './NotFound';
import { POSTS, CATEGORIAS, buscarPost, noIdioma, slugNo, tituloCurto } from '../config/posts';
import { SITE_URL } from '../config/seo';
import { Link, Navigate, caminhoNo, useIdioma } from '../i18n';
import { useFormato } from '../i18n/formato';
import '../styles/NewHome.css';
import '../styles/Palco.css';
import '../styles/Notas.css';
import '../styles/NewBlogPost.css';

/**
 * Leitura de um post, no desenho da página inicial nova. Convive com
 * `pages/BlogPost.js` sem tocar nele: qual das duas atende `/blog/:slug` é o
 * import em `App.js`.
 *
 * Do antigo ficam o índice derivado dos `h2`, o schema e o 404 para slug
 * desconhecido. Muda a abertura, que leva o texto à primeira tela ao lado da
 * figura real do post, a leitura em 18px e o fechamento com a ação da home.
 */

const DATA_LONGA = { day: 'numeric', month: 'long', year: 'numeric' };

/** `Título da seção` → `titulo-da-secao`, para virar âncora. */
const paraId = (texto) =>
  texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

/* O progresso de leitura mora aqui, e não na página: a cada quadro de rolagem
   só o Header desenha de novo, e não o artigo inteiro. */
const HeaderDeLeitura = () => {
  const [progresso, setProgresso] = useState(0);

  useEffect(() => {
    let quadro = 0;
    const medir = () => {
      quadro = 0;
      const raiz = document.documentElement;
      const max = raiz.scrollHeight - raiz.clientHeight;
      setProgresso(max > 0 ? (raiz.scrollTop / max) * 100 : 0);
    };
    const aoRolar = () => {
      if (!quadro) quadro = requestAnimationFrame(medir);
    };
    medir();
    window.addEventListener('scroll', aoRolar, { passive: true });
    return () => {
      window.removeEventListener('scroll', aoRolar);
      cancelAnimationFrame(quadro);
    };
  }, []);

  return <Header progress={progresso} />;
};

const NewBlogPost = ({ user }) => {
  const { slug } = useParams();
  const { t, idioma } = useIdioma();
  const { data } = useFormato();
  const raiz = useRef(null);
  const indiceRef = useRef(null);
  const [ativa, setAtiva] = useState(null);
  const [copiado, setCopiado] = useState('');
  useRevelar(raiz);

  const post = buscarPost(slug);
  const corpo = useMemo(() => (post ? noIdioma(post.corpo, idioma) || [] : []), [post, idioma]);
  const indice = useMemo(
    () => corpo.filter((b) => b.tipo === 'h2').map((b) => ({ id: paraId(b.texto), rotulo: b.texto })),
    [corpo]
  );

  /* Seção ativa: a última cujo título já passou do terço superior da tela. */
  useEffect(() => {
    setAtiva(indice[0]?.id || null);
    if (indice.length < 2 || !('IntersectionObserver' in window)) return undefined;
    const passadas = new Set();
    const observador = new IntersectionObserver(
      (itens) => {
        for (const item of itens) {
          if (item.isIntersecting || item.boundingClientRect.top < 0) passadas.add(item.target.id);
          else passadas.delete(item.target.id);
        }
        setAtiva(indice.filter((s) => passadas.has(s.id)).pop()?.id || indice[0].id);
      },
      { rootMargin: '0px 0px -66% 0px' }
    );
    indice.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observador.observe(el);
    });
    return () => observador.disconnect();
  }, [indice]);

  /* No telefone o índice começa fechado: aberto, empurraria o texto para baixo. */
  useEffect(() => {
    if (indiceRef.current && window.matchMedia('(max-width: 1100px)').matches) indiceRef.current.open = false;
  }, [slug]);

  if (!post) return <NotFound user={user} />;
  /* Slug de outro idioma — o endereço de antes de `slugs`, ou um link colado. */
  if (slug !== slugNo(post, idioma)) return <Navigate to={`/blog/${post.slug}`} replace />;

  const url = `${SITE_URL}${caminhoNo(idioma, `/blog/${post.slug}`)}`;
  const titulo = tituloCurto(post, idioma);
  const categoria = CATEGORIAS[post.categoria];
  const temTela = post.capa?.figura === 'nota';
  const relacionados = POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  const marca = {
    '@type': 'Organization',
    name: 'Silent Puma',
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/marca/app-icon-512.png`
  };
  /* `<` escapado: um `</script>` no texto de um post fecharia a tag. */
  const schema = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BlogPosting',
        headline: titulo,
        description: noIdioma(post.resumo, idioma),
        datePublished: post.data,
        inLanguage: idioma,
        url,
        mainEntityOfPage: url,
        image: marca.logo,
        author: marca,
        publisher: marca
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Silent Puma', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}${caminhoNo(idioma, '/blog')}` },
          { '@type': 'ListItem', position: 3, name: titulo, item: url }
        ]
      }
    ]
  }).replace(/</g, '\\u003c');

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(t('notas.linkCopiado'));
    } catch {
      setCopiado(t('notas.naoCopiou'));
    }
    setTimeout(() => setCopiado(''), 2400);
  };

  const bloco = (b, i) => {
    if (b.tipo === 'h2') return <h2 id={paraId(b.texto)} key={i}>{b.texto}</h2>;
    if (b.tipo === 'lista') {
      return (
        <ul className="nh-lista" key={i}>
          {b.itens.map((item) => <li key={item}>{item}</li>)}
        </ul>
      );
    }
    if (b.tipo === 'nota') return <aside className="post-nota" key={i}>{b.texto}</aside>;
    if (b.tipo === 'calculadora') return <Calculadora key={i} />;
    return <p key={i}>{b.texto}</p>;
  };

  return (
    <>
      <HeaderDeLeitura />

      <main className="nh post-novo" ref={raiz}>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: schema }} />

        <header className={temTela ? 'nh-grade post-cabeca' : 'nh-grade post-cabeca is-so-texto'}>
          <div className="post-texto">
            <nav className="post-trilha" aria-label={t('notas.trilha')}>
              <Link to="/blog">{t('site.blog')}</Link>
              <span aria-hidden="true">/</span>
              <span>{t(categoria.chave)}</span>
            </nav>
            <h1>{titulo}</h1>
            <p className="post-abertura">{noIdioma(post.resumo, idioma)}</p>
            <p className="post-meta">
              <time dateTime={post.data}>{data(post.data, DATA_LONGA)}</time> ·{' '}
              {t('notas.minutos', { n: post.leitura })}
            </p>
          </div>

          {temTela && (
            <figure className="post-tela">
              <div className="post-tela-quadro">
                <TelaDaExecucao estado="pronta" />
              </div>
              <figcaption>{t('notas.legendaTela')}</figcaption>
            </figure>
          )}
        </header>

        <div className="nh-grade post-corpo">
          {indice.length > 1 && (
            <details className="post-indice" open ref={indiceRef}>
              <summary>{t('notas.nestePost')}</summary>
              <ol>
                {indice.map((s) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} aria-current={ativa === s.id ? 'true' : undefined}>
                      {s.rotulo}
                    </a>
                  </li>
                ))}
              </ol>
            </details>
          )}

          <article className="post-artigo">{corpo.map(bloco)}</article>

          <aside className="post-trilho" aria-label={t('notas.compartilhar')}>
            <button type="button" className="post-copiar" onClick={copiar}>
              {t('notas.copiarLink')}
            </button>
            <span className="post-copiado" role="status">{copiado}</span>
          </aside>
        </div>

        {relacionados.length > 0 && (
          <section className="nh-grade notas-lista" aria-labelledby="relacionados-titulo">
            <h2 id="relacionados-titulo">{t('notas.leiaTambem')}</h2>
            <ol className="notas-linhas">
              {relacionados.map((p) => <LinhaDoPost post={p} key={p.slug} />)}
            </ol>
          </section>
        )}

        <Chamada titulo={t('notas.fimPost')} />
      </main>

      <Fecho />
    </>
  );
};

export default NewBlogPost;
